import React, { ChangeEventHandler, useState } from "react";
import { useTranslation } from 'react-i18next';
import { Destination, DestinationType, Organization } from "../common/ObjectTypes";
import Pagination from "../common/Pagination";
import { SortedDown, SortedUp, SortingArrows } from "../common/ProjectIcons";
import DestinationFilter from "./DestinationFilter";
import NoResults from "../common/NoResults";
import { page_size } from "../../api/apiUtils";
import { containerClasses, textClasses, buttonClasses, tableClasses, paginationClasses } from "../common/tailwindClassnames";

type DestinationsListProps = {
    canAddDestination: boolean,
    reloadDestinations: boolean,
    destinations: Destination[],
    destinationTypes: DestinationType[],
    destinationUrls: string[],
    loadingDestinationUrls: boolean,
    loadMoreDestinationUrls: boolean,
    onLoadMoreDestinationUrls: Function,
    organizations: Organization[],
    onAddDestination: Function,
    onViewDetails: Function,
    onChangeEndpoint: ChangeEventHandler,
    onChangeState: ChangeEventHandler,
    onChangeType: ChangeEventHandler,
    onChangeDestinationTypeSearchBar: ChangeEventHandler,
    loadingDestinationTypes: boolean,
    loadMoreDestinationTypes: boolean,
    onLoadMoreDestinationTypes: Function,
    onChangeOwner: ChangeEventHandler,
    onLoadMoreOrganizations: Function,
    onChangeOwnerSearchBar: ChangeEventHandler,
    loadMoreOrganizations: boolean,
    loadingOrganizations: boolean,
    onChangeDestinationUrlSearchBar: ChangeEventHandler,
    onChangeGlobalSearch: Function,
    onRemoveTag: any,
    onResetFilters: any,
    filters: any[],
    globalSearchStr: string,
    requestSort: Function,
    onPageClicked: Function,
    totalCount: number,
    pageLoaded: boolean,
    take: number,
    offset: number
}

const DestinationsList = ({
    canAddDestination,
    reloadDestinations,
    destinations,
    destinationTypes,
    destinationUrls,
    loadingDestinationUrls,
    loadMoreDestinationUrls,
    onLoadMoreDestinationUrls,
    organizations,
    onAddDestination,
    onViewDetails,
    onChangeEndpoint,
    onChangeState,
    onChangeType,
    onChangeDestinationTypeSearchBar,
    loadingDestinationTypes,
    loadMoreDestinationTypes,
    onLoadMoreDestinationTypes,
    onChangeOwner,
    onChangeOwnerSearchBar,
    loadMoreOrganizations,
    loadingOrganizations,
    onLoadMoreOrganizations,
    onChangeDestinationUrlSearchBar,
    onChangeGlobalSearch,
    onRemoveTag,
    onResetFilters,
    filters,
    globalSearchStr,
    requestSort,
    onPageClicked,
    totalCount,
    pageLoaded,
    take,
    offset
}: DestinationsListProps) => {
    const { t } = useTranslation();
    const [sortDirection, setSortDirection] = useState(false);
    const [sortByColumn, setSortByColumn] = useState("name");

    return (
        <div className="bg-very-light-gray dark:bg-off-black transition-colors duration-300">
            <div className="mb-3 flex justify-between">
                <div>
                    <h1 data-testid="destinations_page_header" className="text-3xl font-bold mb-3 dark:text-white transition-colors duration-300">
                        {t("Destinations")}
                    </h1>
                </div>
                {canAddDestination && <button className="bg-dark-green hover:bg-status-green text-white font-bold rounded px-7 transition-colors duration-300"
                    onClick={() => onAddDestination(null)}>
                    {t("Create Destination")}
                </button>}
            </div>
            <div className="flex flex-row items-start w-full">
                {/* FILTERS */}
                <DestinationFilter
                    destinationUrls={destinationUrls}
                    destinationTypes={destinationTypes}
                    organizations={organizations}
                    filters={filters}
                    globalSearchStr={globalSearchStr}
                    onChangeGlobalSearch={onChangeGlobalSearch}
                    onRemoveTag={onRemoveTag}
                    onResetFilters={onResetFilters}
                    onChangeDestinationUrlSearchBar={onChangeDestinationUrlSearchBar}
                    onChangeEndpoint={onChangeEndpoint}
                    onChangeState={onChangeState}
                    onChangeType={onChangeType}
                    onChangeDestinationTypeSearchBar={onChangeDestinationTypeSearchBar}
                    loadingDestinationTypes={loadingDestinationTypes}
                    loadMoreDestinationTypes={loadMoreDestinationTypes}
                    onLoadMoreDestinationTypes={onLoadMoreDestinationTypes}
                    onChangeOwnerSearchBar={onChangeOwnerSearchBar}
                    onLoadMoreOrganizations={onLoadMoreOrganizations}
                    loadingOrganizations={loadingOrganizations}
                    loadMoreOrganizations={loadMoreOrganizations}
                    onChangeOwner={onChangeOwner}
                    showStatus={true}
                    showUrlFilter={true}
                    loadingDestinationUrls={loadingDestinationUrls}
                    loadMoreDestinationUrls={loadMoreDestinationUrls}
                    onLoadMoreDestinationUrls={onLoadMoreDestinationUrls}
                />
                <div className="flex flex-col w-full">


                    {destinations.length < 1 &&
                        <NoResults
                            headerMsg={t("No Results Found")}
                            labelMsg={t("You may need to adjust your search or filters to find what you are looking for")}
                        />}

                    {destinations.length >= 1 &&
                        <table className="table-auto h-auto w-full cursor-pointer border border-gray-lines dark:border-secondary-gray text-off-black dark:text-white rounded-md bg-very-light-gray dark:bg-off-black transition-colors duration-300">
                            <thead className="px-3 pt-3 pb-2 font-bold uppercase text-sm bg-light-gray dark:bg-dark-gray text-left transition-colors duration-300">
                                <tr>
                                    <th className="p-3 dark:text-white transition-colors duration-300">
                                        {t("Destination")}
                                        <span onClick={() => {
                                            requestSort("name", sortDirection)
                                            setSortDirection(!sortDirection)
                                            setSortByColumn("name")
                                        }}>
                                            {sortByColumn === "name" ?
                                                sortDirection ? <SortedUp /> : <SortedDown />
                                                : <SortingArrows />}
                                        </span>
                                    </th>
                                    <th className="p-3 dark:text-white transition-colors duration-300">
                                        {t("URL")}
                                        <span onClick={() => {
                                            requestSort("endpoint", sortDirection)
                                            setSortDirection(!sortDirection)
                                            setSortByColumn("endpoint")
                                        }}>
                                            {sortByColumn === "endpoint" ?
                                                sortDirection ? <SortedUp /> : <SortedDown />
                                                : <SortingArrows />}
                                        </span>
                                    </th>
                                    <th className="p-3 dark:text-white transition-colors duration-300">
                                        {t("Technology")}
                                        <span onClick={() => {
                                            requestSort("type__name", sortDirection)
                                            setSortDirection(!sortDirection)
                                            setSortByColumn("type__name")
                                        }}>
                                            {sortByColumn === "type__name" ?
                                                sortDirection ? <SortedUp /> : <SortedDown />
                                                : <SortingArrows />}
                                        </span>
                                    </th>
                                    <th className="p-3 dark:text-white transition-colors duration-300">
                                        {t("Workspace")}
                                        <span onClick={() => {
                                            requestSort("owner__name", sortDirection)
                                            setSortDirection(!sortDirection)
                                            setSortByColumn("owner__name")
                                        }}>
                                            {sortByColumn === "owner__name" ?
                                                sortDirection ? <SortedUp /> : <SortedDown />
                                                : <SortingArrows />}
                                        </span>
                                    </th>
                                </tr>
                            </thead>
                            {reloadDestinations ?
                                <tbody className="p-6">
                                    <tr>
                                        <td className="p-6">...</td>
                                        <td className="p-6">...</td>
                                        <td className="p-6">...</td>
                                        <td className="p-6">...</td>
                                    </tr>
                                </tbody>
                                :
                                <tbody className="text-sm dark:text-neutral-gray-400 transition-colors duration-300">
                                    {destinations.map((destination: Destination) => {
                                        let dest_url: string | URL;
                                        try {
                                            dest_url = new URL(destination.base_url).hostname
                                        } catch (err) {
                                            dest_url = ""
                                        }
                                        return (
                                            <tr key={destination.id}
                                                className="border border-gray-lines dark:border-secondary-gray hover:bg-light-blue dark:hover:bg-neutral-gray-700 bg-white dark:bg-off-black transition-colors duration-300"
                                                onClick={() => onViewDetails(destination.id)}
                                            >
                                                <td className="p-3 dark:text-white transition-colors duration-300">
                                                    {destination.name}
                                                </td>
                                                <td className="p-3 text-blue-basic dark:text-dark-blue-300 transition-colors duration-300">
                                                    {dest_url}
                                                </td>
                                                <td className="p-3 dark:text-white transition-colors duration-300">
                                                    {destination.type.name}
                                                </td>
                                                <td className="p-3 dark:text-white transition-colors duration-300">
                                                    {destination.owner.name}
                                                </td>
                                            </tr>
                                        )
                                    })}
                                </tbody>}
                        </table>}
                    {totalCount > page_size && <Pagination
                        showText={true}
                        handlePageClick={onPageClicked}
                        pageCount={Math.ceil(totalCount / take)}
                        resultStart={offset + 1}
                        resultEnd={(offset + take) < totalCount ? (offset + take) : totalCount}
                        totalCount={totalCount}
                        take={take}
                        loading={!pageLoaded}
                        mainclassname="w-full mt-2 flex flex-row justify-between bg-very-light-gray dark:bg-neutral-gray-800 opacity-80 dark:text-neutral-gray-400 transition-colors duration-300"
                    />}
                </div>
            </div>
        </div>
    )
};

export default DestinationsList;